<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import IconBase from '../icon/IconBase.vue'
import IconLinkExternal from '../icon/IconLinkExternal.vue'
import IconBin from '../icon/IconBin.vue'
import IconLinkStroke from '../icon/IconLinkStroke.vue'
import IconPlusStroke from '../icon/IconPlusStroke.vue'
import FormButton from '../Atoms/FormButton.vue'
import { formatMoney } from '@/Utils/money'

const props = defineProps({
	paywall: { type: Object, required: true },
	earnings: { type: [Number, String], default: 0 },
	customers: { type: Number, default: 0 },
})

defineEmits(['open', 'toggle', 'delete'])

const router = useRouter()

// Full URL to this paywall's manage/settings page, so it can be opened in a
// new tab (respects the app's base path).
const manageUrl = computed(() =>
	router.resolve({ path: '/paywall/v2/' + props.paywall.id }).href,
)

const hasSource = computed(() => !!props.paywall?.source)

const sourceType = computed(() => {
	const s = props.paywall?.source
	if (!s) return ''
	if (s.streams) return 'Playlist'
	switch (s.type) {
		case 'live': return 'Livestream'
		case 'restream': return 'Multistream'
		case 'vod': return 'VOD'
		case 'ipcam': return 'IP Camera'
		case 'subsecond': return 'Sub Second'
		default: return s.type || 'Source'
	}
})

// Explicit, labeled status (disabled > no-source > active)
const status = computed(() => {
	if (!props.paywall?.enabled) {
		return { label: 'Disabled', text: 'text-surface-7', bg: 'bg-surface-3', dot: 'bg-surface-6' }
	}
	if (!hasSource.value) {
		return { label: 'No source', text: 'text-gold', bg: 'bg-surface-3', dot: 'bg-gold' }
	}
	return { label: 'Active', text: 'text-success', bg: 'bg-success-transparent', dot: 'bg-success' }
})
</script>

<template>
	<div role="button" tabindex="0" @click="$emit('open')" @keydown.enter="$emit('open')" @keydown.space.prevent="$emit('open')"
		class="group flex flex-col bg-surface-2 rounded-xl ring-1 ring-white/[0.06] shadow-[0_2px_6px_rgba(0,0,0,0.28)] cursor-pointer transition-[box-shadow,transform,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(0,0,0,0.5)] hover:ring-white/[0.12] focus:outline-none focus-visible:ring-main">
		<div class="flex flex-col gap-3 p-4 grow">
			<!-- Title + status -->
			<div class="flex items-start justify-between gap-2">
				<h3 class="text-sm font-semibold text-white truncate group-hover:text-main-light transition-colors mb-0 min-w-0">
					{{ paywall.internalName || paywall.title }}
				</h3>
				<span
					class="shrink-0 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium leading-none"
					:class="[status.bg, status.text]">
					<span class="size-1.5 rounded-full" :class="status.dot"></span>
					{{ status.label }}
				</span>
			</div>

			<!-- Source -->
			<div v-if="hasSource" class="flex items-center gap-1.5 text-tiny text-surface-7 min-w-0">
				<icon-base class="shrink-0 text-surface-6" :width="13" :height="13" width-view-box="24" height-view-box="24"
					fill="none">
					<icon-link-stroke />
				</icon-base>
				<span class="truncate">{{ paywall.source.name }}</span>
				<span class="shrink-0 text-surface-6">·&nbsp;{{ sourceType }}</span>
			</div>
			<FormButton v-else
				custom-classes="inline-flex w-fit items-center gap-1.5 text-tiny font-medium text-gold hover:underline"
				@click.stop="$emit('open')">
				<icon-base class="shrink-0" :width="14" :height="14" width-view-box="24" height-view-box="24" fill="none">
					<icon-plus-stroke />
				</icon-base>
				Add a source
			</FormButton>

			<!-- Price -->
			<div class="flex items-baseline gap-2 min-w-0">
				<span class="text-lg font-semibold text-white leading-none truncate">{{ formatMoney(paywall.price, paywall.currency) }}</span>
				<span class="shrink-0 text-[11px] text-surface-6">Pay-per-view</span>
			</div>

			<!-- Stats -->
			<div class="mt-auto grid grid-cols-2 gap-3 rounded-lg bg-surface-1 p-3">
				<div class="min-w-0">
					<p class="text-[10px] font-semibold uppercase tracking-wider text-surface-6 mb-0.5">Earnings</p>
					<p class="text-sm font-medium text-white mb-0 truncate">{{ formatMoney(earnings, paywall.currency) }}</p>
				</div>
				<div class="min-w-0">
					<p class="text-[10px] font-semibold uppercase tracking-wider text-surface-6 mb-0.5">Customers</p>
					<p class="text-sm font-medium text-white mb-0 truncate">{{ customers }}</p>
				</div>
			</div>
		</div>

		<!-- Footer actions -->
		<div class="flex items-center justify-between gap-2 px-3 pb-3">
			<FormButton @click.stop="$emit('toggle')"
				:custom-classes="['inline-flex items-center h-8 px-2.5 rounded-lg text-tiny font-medium transition-colors',
					paywall.enabled
						? 'text-surface-7 hover:text-white hover:bg-surface-3'
						: 'text-success hover:bg-success-transparent']">
				{{ paywall.enabled ? 'Disable' : 'Enable' }}
			</FormButton>
			<div class="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200">
				<a :href="manageUrl" target="_blank" rel="noopener noreferrer" title="Open in new tab"
					@click.stop
					class="inline-flex items-center justify-center size-8 rounded-lg text-surface-7 hover:text-white hover:bg-surface-3 transition-colors">
					<icon-base fill="none">
						<icon-link-external />
					</icon-base>
				</a>
			<FormButton title="Delete" @click.stop="$emit('delete')"
				custom-classes="inline-flex items-center justify-center size-8 rounded-lg text-surface-7 hover:text-danger hover:bg-surface-3 transition-colors">
				<icon-base fill="none">
					<icon-bin />
				</icon-base>
			</FormButton>
			</div>
		</div>
	</div>
</template>
